import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Translate } from 'react-redux-i18n';
import {
  FranchiseSnapshotModel,
  FranchiseEditionType,
  ViewMode,
  MetricKey
} from '../types';
import { processEditionsForNamedView, processEditionsForYearView } from '../utils';
import { EditionGroup } from './EditionGroup';
import { YearGroup } from './YearGroup';

export interface EditionsListProps {
  franchise: FranchiseSnapshotModel;
  includeOffline: boolean;
  showAllMetrics: boolean;
  isSystemAdmin: boolean;
  isAnimating: boolean;
  selectedMetric: MetricKey | null;
}

export const EditionsList: React.FC<EditionsListProps> = ({
  franchise,
  includeOffline,
  showAllMetrics,
  isSystemAdmin,
  isAnimating,
  selectedMetric
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('named');
  const [expandAll, setExpandAll] = useState(false);
  const isNamedFranchise = franchise.editionType === FranchiseEditionType.NAMED;

  // Determine which processing to use based on franchise type AND view mode
  // For BY_YEAR franchises: always use year view, ignore viewMode
  // For NAMED franchises: respect viewMode (user can switch between views)
  const shouldUseYearView = 
    franchise.editionType === FranchiseEditionType.BY_YEAR || 
    (isNamedFranchise && viewMode === 'byYear');

  const namedEditions =
    !shouldUseYearView ? processEditionsForNamedView(franchise) : [];
  const yearGroups =
    shouldUseYearView ? processEditionsForYearView(franchise) : {};

  const yearsSorted = Object.keys(yearGroups)
    .map(Number)
    .sort((a, b) => b - a); // Descending

  return (
    <div className="franchise-editions-list">
      {/* Tabs for named franchises */}
      {isNamedFranchise && (
        <div className="franchise-view-tabs">
          <button
            className={`franchise-view-tab ${viewMode === 'named' ? 'active' : ''}`}
            onClick={() => setViewMode('named')}
          >
            <Translate value="franchise.hub.tabs.named" />
          </button>
          <button
            className={`franchise-view-tab ${viewMode === 'byYear' ? 'active' : ''}`}
            onClick={() => setViewMode('byYear')}
          >
            <Translate value="franchise.hub.tabs.year" />
          </button>
        </div>
      )}

      {/* Expand/Collapse All Button */}
      <div className="franchise-expand-controls">
        <button
          className="franchise-expand-all-btn"
          onClick={() => setExpandAll(!expandAll)}
        >
          {expandAll ? (
            <>
              <ChevronUp className="franchise-expand-icon" />
              <Translate value="franchise.hub.controls.collapse-all" />
            </>
          ) : (
            <>
              <ChevronDown className="franchise-expand-icon" />
              <Translate value="franchise.hub.controls.expand-all" />
            </>
          )}
        </button>
      </div>

      {/* Named View */}
      {!shouldUseYearView && (
        <div className="franchise-named-view">
          {namedEditions.map(edition => (
            <EditionGroup
              key={edition.edition.id}
              edition={edition}
              includeOffline={includeOffline}
              showAllMetrics={showAllMetrics}
              isSystemAdmin={isSystemAdmin}
              isAnimating={isAnimating}
              expandAll={expandAll}
              selectedMetric={selectedMetric}
            />
          ))}
        </div>
      )}

      {/* Year View */}
      {shouldUseYearView && (
        <div className="franchise-year-view">
          {yearsSorted.map(year => (
            <YearGroup
              key={year}
              year={year}
              entities={yearGroups[year]}
              includeOffline={includeOffline}
              showAllMetrics={showAllMetrics}
              isSystemAdmin={isSystemAdmin}
              isAnimating={isAnimating}
              expandAll={expandAll}
              selectedMetric={selectedMetric}
            />
          ))}
        </div>
      )}

      {/* Empty state */}
      {namedEditions.length === 0 && yearsSorted.length === 0 && (
        <div className="franchise-editions-empty">
          <Translate value="franchise.hub.editions.no-data" />
        </div>
      )}
    </div>
  );
};
